import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
export function PrivacyPolicyPage() {
  return <>
      <Helmet data-id="element-9634">
        <title data-id="element-9635">Privacy Policy | ToxicDetector</title>
        <meta name="description" content="Learn how ToxicDetector protects your data and privacy when analyzing messages for toxicity and manipulation." data-id="element-9636" />
        <link rel="canonical" href="https://toxicdetector.com/privacy" data-id="element-9637" />
        <meta property="og:title" content="Privacy Policy | ToxicDetector" data-id="element-9638" />
        <meta property="og:description" content="Learn how ToxicDetector protects your data and privacy when analyzing messages for toxicity and manipulation." data-id="element-9639" />
        <meta property="og:url" content="https://toxicdetector.com/privacy" data-id="element-9640" />
        <meta property="og:type" content="website" data-id="element-9641" />
        <meta name="robots" content="index, follow" data-id="element-9642" />
      </Helmet>
      <div className="flex flex-col w-full min-h-screen bg-[#f8f6f2]" data-id="element-9643">
        <header className="bg-white border-b border-gray-200" data-id="element-9644">
          <div className="max-w-4xl mx-auto px-5 py-4" data-id="element-9645">
            <Link to="/" className="inline-flex items-center text-gray-600 hover:text-purple-600 text-sm mb-2" data-id="element-9646">
              <ArrowLeft size={16} className="mr-1" data-id="element-9647" />
              Back to Home
            </Link>
            <h1 className="text-2xl font-bold" data-id="element-9648">Privacy Policy</h1>
            <p className="text-gray-600 text-sm mt-1" data-id="element-9649">
              Last updated:{' '}
              {new Date().toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            })}
            </p>
          </div>
        </header>

        <div className="max-w-4xl mx-auto w-full px-5 py-8" data-id="element-9650">
          <div className="bg-white rounded-xl p-8 shadow-sm" data-id="element-9651">
            <section className="mb-8" data-id="element-9652">
              <h2 className="text-xl font-bold mb-4" data-id="element-9653">Introduction</h2>
              <p className="text-gray-700 mb-4" data-id="element-9654">
                Welcome to ToxicDetector ("we," "our," or "us"). We are
                committed to protecting your privacy and ensuring the security
                of your personal information. This Privacy Policy explains how
                we collect, use, disclose, and safeguard your information when
                you use our web application and services.
              </p>
              <p className="text-gray-700" data-id="element-9655">
                By using ToxicDetector, you agree to the collection and use of
                information in accordance with this Privacy Policy. If you do
                not agree with our policies and practices, please do not use our
                services.
              </p>
            </section>

            <section className="mb-8" data-id="element-9656">
              <h2 className="text-xl font-bold mb-4" data-id="element-9657">
                1. Information We Collect
              </h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-9658">
                1.1 Information You Provide
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-9659">
                We collect information that you voluntarily provide when using
                our services:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-9660">
                <li data-id="element-9661">
                  <strong data-id="element-9662">Account Information:</strong> Name, email address,
                  password (encrypted), and profile details
                </li>
                <li data-id="element-9663">
                  <strong data-id="element-9664">Uploaded Content:</strong> Screenshots and images of
                  text conversations you upload for analysis
                </li>
                <li data-id="element-9665">
                  <strong data-id="element-9666">Analysis Data:</strong> Results from toxicity
                  analysis, including detected patterns and risk scores
                </li>
                <li data-id="element-9667">
                  <strong data-id="element-9668">Communication Preferences:</strong> Settings for
                  notifications, marketing emails, and data sharing preferences
                </li>
              </ul>

              <h3 className="text-lg font-semibold mb-3" data-id="element-9669">
                1.2 Automatically Collected Information
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-9670">
                When you access our services, we automatically collect certain
                information:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-9671">
                <li data-id="element-9672">
                  <strong data-id="element-9673">Usage Data:</strong> Pages visited, features used,
                  time spent on the platform, and interaction patterns
                </li>
                <li data-id="element-9674">
                  <strong data-id="element-9675">Device Information:</strong> Browser type, operating
                  system, device identifiers, and IP address
                </li>
                <li data-id="element-9676">
                  <strong data-id="element-9677">Authentication Data:</strong> Login timestamps,
                  session information, and security events
                </li>
                <li data-id="element-9678">
                  <strong data-id="element-9679">Analytics Data:</strong> Aggregated usage statistics
                  and performance metrics
                </li>
              </ul>

              <h3 className="text-lg font-semibold mb-3" data-id="element-9680">
                1.3 Cookies and Tracking Technologies
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-9681">
                We use cookies and similar tracking technologies to enhance your
                experience, maintain session security, and analyze usage
                patterns. You can control cookie preferences through your
                browser settings, though some features may not function properly
                if cookies are disabled.
              </p>
            </section>

            <section className="mb-8" data-id="element-9682">
              <h2 className="text-xl font-bold mb-4" data-id="element-9683">
                2. How We Use Your Information
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-9684">
                We use the collected information for the following purposes:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-9685">
                <li data-id="element-9686">
                  <strong data-id="element-9687">Service Delivery:</strong> To provide, maintain, and
                  improve our toxicity detection and analysis services
                </li>
                <li data-id="element-9688">
                  <strong data-id="element-9689">AI Analysis:</strong> To process uploaded screenshots
                  using artificial intelligence to detect toxic patterns in
                  conversations
                </li>
                <li data-id="element-9690">
                  <strong data-id="element-9691">Account Management:</strong> To create and manage your
                  account, authenticate your identity, and provide customer
                  support
                </li>
                <li data-id="element-9692">
                  <strong data-id="element-9693">Communication:</strong> To send you service updates,
                  security alerts, and (with your consent) marketing
                  communications
                </li>
                <li data-id="element-9694">
                  <strong data-id="element-9695">Security:</strong> To detect, prevent, and address
                  technical issues, fraud, and security vulnerabilities
                </li>
                <li data-id="element-9696">
                  <strong data-id="element-9697">Analytics:</strong> To understand usage patterns,
                  improve our algorithms, and enhance user experience
                </li>
                <li data-id="element-9698">
                  <strong data-id="element-9699">Legal Compliance:</strong> To comply with legal
                  obligations and enforce our Terms of Service
                </li>
              </ul>
            </section>

            <section className="mb-8" data-id="element-9700">
              <h2 className="text-xl font-bold mb-4" data-id="element-9701">
                3. How We Share Your Information
              </h2>

              <h3 className="text-lg font-semibold mb-3" data-id="element-9702">
                3.1 Third-Party Service Providers
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-9703">
                We share information with trusted third-party service providers
                who assist in operating our platform:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-9704">
                <li data-id="element-9705">
                  <strong data-id="element-9706">Supabase:</strong> Cloud infrastructure for
                  authentication, database, and file storage
                </li>
                <li data-id="element-9707">
                  <strong data-id="element-9708">OpenAI:</strong> AI processing for toxicity analysis
                  (text content only, not personal identifiers)
                </li>
                <li data-id="element-9709">
                  <strong data-id="element-9710">Analytics Providers:</strong> For usage analytics and
                  performance monitoring (when enabled in your settings)
                </li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-9711">
                These providers are contractually obligated to protect your
                information and use it only for the specific services they
                provide to us.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-9712">3.2 Public Sharing</h3>
              <p className="text-gray-700 mb-4" data-id="element-9713">
                When you choose to share analysis results using our sharing
                feature, a public link is generated that allows anyone with the
                link to view the analysis results. Shared results do not include
                your personal information but do include the analysis data and
                patterns detected.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-9714">
                3.3 Legal Requirements
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-9715">
                We may disclose your information if required by law, court
                order, or governmental regulation, or if we believe disclosure
                is necessary to protect our rights, your safety, or the safety
                of others.
              </p>

              <h3 className="text-lg font-semibold mb-3" data-id="element-9716">
                3.4 Business Transfers
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-9717">
                In the event of a merger, acquisition, or sale of assets, your
                information may be transferred to the acquiring entity. We will
                notify you of any such change and provide options regarding your
                information.
              </p>
            </section>

            <section className="mb-8" data-id="element-9718">
              <h2 className="text-xl font-bold mb-4" data-id="element-9719">
                4. Data Retention and Storage
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-9720">
                We retain your information for as long as necessary to provide
                our services and comply with legal obligations:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-9721">
                <li data-id="element-9722">
                  <strong data-id="element-9723">Account Data:</strong> Retained while your account is
                  active and for 90 days after account deletion
                </li>
                <li data-id="element-9724">
                  <strong data-id="element-9725">Uploaded Screenshots:</strong> Stored according to
                  your retention settings (30, 90, 180 days, or indefinitely)
                </li>
                <li data-id="element-9726">
                  <strong data-id="element-9727">Analysis Results:</strong> Retained according to your
                  chosen retention period
                </li>
                <li data-id="element-9728">
                  <strong data-id="element-9729">Usage Logs:</strong> Retained for up to 12 months for
                  security and analytics purposes
                </li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-9730">
                You can manage your data retention preferences in the Settings
                section under Privacy Settings. You can also request deletion of
                specific scans or your entire account at any time.
              </p>
            </section>

            <section className="mb-8" data-id="element-9731">
              <h2 className="text-xl font-bold mb-4" data-id="element-9732">5. Data Security</h2>
              <p className="text-gray-700 mb-4" data-id="element-9733">
                We implement industry-standard security measures to protect your
                information:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-9734">
                <li data-id="element-9735">
                  <strong data-id="element-9736">Encryption:</strong> All data is encrypted in transit
                  (TLS/SSL) and at rest
                </li>
                <li data-id="element-9737">
                  <strong data-id="element-9738">Authentication:</strong> Secure password hashing and
                  optional two-factor authentication (2FA)
                </li>
                <li data-id="element-9739">
                  <strong data-id="element-9740">Access Controls:</strong> Strict access controls and
                  authentication for all data access
                </li>
                <li data-id="element-9741">
                  <strong data-id="element-9742">Regular Audits:</strong> Periodic security assessments
                  and vulnerability testing
                </li>
                <li data-id="element-9743">
                  <strong data-id="element-9744">Secure Infrastructure:</strong> Hosted on secure, SOC
                  2 compliant cloud infrastructure
                </li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-9745">
                While we strive to protect your information, no method of
                transmission or storage is 100% secure. We cannot guarantee
                absolute security but continuously work to improve our security
                measures.
              </p>
            </section>

            <section className="mb-8" data-id="element-9746">
              <h2 className="text-xl font-bold mb-4" data-id="element-9747">6. Your Privacy Rights</h2>
              <p className="text-gray-700 mb-4" data-id="element-9748">
                Depending on your location, you may have the following rights
                regarding your personal information:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-9749">
                <li data-id="element-9750">
                  <strong data-id="element-9751">Access:</strong> Request a copy of the personal
                  information we hold about you
                </li>
                <li data-id="element-9752">
                  <strong data-id="element-9753">Correction:</strong> Update or correct inaccurate
                  personal information
                </li>
                <li data-id="element-9754">
                  <strong data-id="element-9755">Deletion:</strong> Request deletion of your personal
                  information (subject to legal retention requirements)
                </li>
                <li data-id="element-9756">
                  <strong data-id="element-9757">Data Portability:</strong> Receive your data in a
                  structured, machine-readable format
                </li>
                <li data-id="element-9758">
                  <strong data-id="element-9759">Opt-Out:</strong> Opt out of marketing communications
                  and certain data processing activities
                </li>
                <li data-id="element-9760">
                  <strong data-id="element-9761">Restriction:</strong> Request restriction of
                  processing in certain circumstances
                </li>
                <li data-id="element-9762">
                  <strong data-id="element-9763">Objection:</strong> Object to processing based on
                  legitimate interests
                </li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-9764">
                To exercise these rights, visit your Settings page or contact us
                at toxicdetector25@gmail.com. We will respond to your request
                within 30 days.
              </p>
            </section>

            <section className="mb-8" data-id="element-9765">
              <h2 className="text-xl font-bold mb-4" data-id="element-9766">
                7. International Data Transfers
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-9767">
                Your information may be transferred to and processed in
                countries other than your country of residence. These countries
                may have different data protection laws. When we transfer your
                information internationally, we ensure appropriate safeguards
                are in place to protect your information in accordance with this
                Privacy Policy.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-9768">
                For users in the European Economic Area (EEA), we comply with
                GDPR requirements for international data transfers, including
                the use of Standard Contractual Clauses where applicable.
              </p>
            </section>

            <section className="mb-8" data-id="element-9769">
              <h2 className="text-xl font-bold mb-4" data-id="element-9770">8. Children's Privacy</h2>
              <p className="text-gray-700 mb-4" data-id="element-9771">
                ToxicDetector is not intended for use by individuals under the
                age of 18. We do not knowingly collect personal information from
                children under 18. If we become aware that we have collected
                personal information from a child under 18, we will take steps
                to delete such information promptly.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-9772">
                If you are a parent or guardian and believe your child has
                provided us with personal information, please contact us at
                toxicdetector25@gmail.com.
              </p>
            </section>

            <section className="mb-8" data-id="element-9773">
              <h2 className="text-xl font-bold mb-4" data-id="element-9774">
                9. California Privacy Rights
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-9775">
                If you are a California resident, you have additional rights
                under the California Consumer Privacy Act (CCPA):
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-9776">
                <li data-id="element-9777">
                  Right to know what personal information is collected, used,
                  shared, or sold
                </li>
                <li data-id="element-9778">Right to delete personal information held by businesses</li>
                <li data-id="element-9779">
                  Right to opt-out of the sale of personal information (we do
                  not sell personal information)
                </li>
                <li data-id="element-9780">Right to non-discrimination for exercising CCPA rights</li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-9781">
                To exercise these rights, contact us at
                toxicdetector25@gmail.com or use the privacy controls in your
                Settings.
              </p>
            </section>

            <section className="mb-8" data-id="element-9782">
              <h2 className="text-xl font-bold mb-4" data-id="element-9783">
                10. European Privacy Rights (GDPR)
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-9784">
                If you are located in the European Economic Area (EEA), you have
                rights under the General Data Protection Regulation (GDPR):
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-9785">
                <li data-id="element-9786">
                  Legal basis for processing: Consent, contract performance,
                  legitimate interests, and legal obligations
                </li>
                <li data-id="element-9787">Right to withdraw consent at any time</li>
                <li data-id="element-9788">
                  Right to lodge a complaint with your local data protection
                  authority
                </li>
                <li data-id="element-9789">
                  Right to data portability in a structured, commonly used
                  format
                </li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-9790">
                Our data protection officer can be reached at
                toxicdetector25@gmail.com for GDPR-related inquiries.
              </p>
            </section>

            <section className="mb-8" data-id="element-9791">
              <h2 className="text-xl font-bold mb-4" data-id="element-9792">
                11. Changes to This Privacy Policy
              </h2>
              <p className="text-gray-700 mb-4" data-id="element-9793">
                We may update this Privacy Policy from time to time to reflect
                changes in our practices, technology, legal requirements, or
                other factors. We will notify you of any material changes by:
              </p>
              <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2" data-id="element-9794">
                <li data-id="element-9795">
                  Posting the updated Privacy Policy on this page with a new
                  "Last Updated" date
                </li>
                <li data-id="element-9796">
                  Sending you an email notification (if you have opted in to
                  receive such notifications)
                </li>
                <li data-id="element-9797">Displaying a prominent notice on our platform</li>
              </ul>
              <p className="text-gray-700 mb-4" data-id="element-9798">
                Your continued use of ToxicDetector after any changes indicates
                your acceptance of the updated Privacy Policy.
              </p>
            </section>

            <section className="mb-8" data-id="element-9799">
              <h2 className="text-xl font-bold mb-4" data-id="element-9800">12. Contact Us</h2>
              <p className="text-gray-700 mb-4" data-id="element-9801">
                If you have questions, concerns, or requests regarding this
                Privacy Policy or our privacy practices, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-9802">
                <p className="text-gray-700 mb-2" data-id="element-9803">
                  <strong data-id="element-9804">Email:</strong> toxicdetector25@gmail.com
                </p>
                <p className="text-gray-700 mb-2" data-id="element-9805">
                  <strong data-id="element-9806">Support:</strong> support@toxicdetector.com
                </p>
                <p className="text-gray-700" data-id="element-9807">
                  <strong data-id="element-9808">Data Protection Officer:</strong>{' '}
                  toxicdetector25@gmail.com
                </p>
              </div>
            </section>

            <section className="mb-8" data-id="element-9809">
              <h2 className="text-xl font-bold mb-4" data-id="element-9810">13. Consent</h2>
              <p className="text-gray-700 mb-4" data-id="element-9811">
                By using ToxicDetector, you consent to our Privacy Policy and
                agree to its terms. If you do not agree with this Privacy
                Policy, please discontinue use of our services immediately.
              </p>
            </section>

            <div className="border-t border-gray-200 pt-6 mt-8" data-id="element-9812">
              <p className="text-gray-600 text-sm text-center" data-id="element-9813">
                © {new Date().getFullYear()} ToxicDetector. All rights
                reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>;
}